import {
  can as canAction,
  getModuleAccess,
  normalizeModuleAccess,
  resolveModuleKey,
} from "../utils/moduleAccess";

export {
  canLookup,
  canCallLookupAction,
  getLookupsForModule,
  LOOKUP_API_ACTIONS,
} from "../utils/lookupAccess";

export const SALES_APPROVAL_PERMISSION = "Sales Approval";

export {
  canAction as can,
  getModuleAccess,
  normalizeModuleAccess,
  resolveModuleKey,
};

const SUPERADMIN_ALLOWED_KEYS = new Set(["dashboard", "user_management", "business_management"]);

export const hasPermission = (key) => {
  const role = String(localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();

  if (role === "admin") {
    return true;
  }


  if (role === "superadmin") {
    return SUPERADMIN_ALLOWED_KEYS.has(key);
  }

  let permissions = [];
  try {
    permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    permissions = [];
  }
  if (!Array.isArray(permissions) || !permissions.includes(key)) return false;

  // Flag / scope keys — presence in permissions[] is enough
  if (
    key === "supplier_master_view" ||
    key === "supplier_master_all" ||
    key === "customer_master_view" ||
    key === "customer_master_all" ||
    key === "tracker_update_import" ||
    key === "tracker_update_export"
  ) {
    return true;
  }

  // If moduleAccess is configured, also require view on that module
  try {
    const moduleAccess = JSON.parse(
      localStorage.getItem("moduleAccess") || "[]"
    );
    if (Array.isArray(moduleAccess) && moduleAccess.length > 0) {
      return canAction(
        { permissions, moduleAccess, role },
        key,
        "view"
      );
    }
  } catch {
    // fall through to permission-only
  }

  return true;
};

/** Flat list kept for compatibility; prefer PERMISSION_GROUPS in role UI. */
export const PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "ai_chat", label: "AI Search" },
  { key: "supply_master", label: "Supplier Master" },
  { key: "procurement_tracker", label: "Purchase Request" },
  { key: "rfq", label: "RFQ" },
  { key: "supplier_rfq", label: "Supplier RFQ" },
  { key: "supplier_po", label: "Supplier PO" },
  { key: "supplier_gi", label: "Supplier GI" },
  { key: "po", label: "Purchase Order" },
  { key: "price_tracker", label: "Price List" },
  { key: "customer_master", label: "Customer Master" },
  { key: "sales_tracker", label: "Sales Tracker" },
  { key: "sales_quotes", label: "Sales Quotes" },
  { key: "sales_order", label: "Sales Orders" },
  { key: SALES_APPROVAL_PERMISSION, label: "Sales Approval" },
  { key: "ecatalogue", label: "E-Catalogue" },
  { key: "so_converter", label: "SO Converter" },
  { key: "tracker_update", label: "Customer Order Report" },
  { key: "tracker_update_import", label: "Customer Order Report Import" },
  { key: "tracker_update_export", label: "Customer Order Report Export" },
  { key: "payment_tracker", label: "Payment Tracker" },
  { key: "freight_tracker", label: "Freight Tracker" },
  { key: "task_management", label: "Task Management" },
  { key: "inventory_management", label: "Inventory Management" },
  { key: "goods_inwards", label: "Goods Inwards" },
  { key: "goods_outwards", label: "Goods Outwards" },
  { key: "category_management", label: "Category" },
  { key: "subcategory_management", label: "Subcategory" },
  { key: "material_management", label: "Material" },
  { key: "unit_management", label: "Unit" },
  { key: "attribute_management", label: "Brand/OEM" },
  { key: "user_management", label: "Users" },
  { key: "role_management", label: "Roles" },
  { key: "business_management", label: "Business" },
  { key: "customer_sq", label: "Customer Sales Quote" },
  { key: "customer_so", label: "Customer Sales Order" },
  { key: "logistics", label: "Logistics RFQ" },

];

export const DEFAULT_CRUD_ACTIONS = ["view", "create", "update", "delete"];

/**
 * Modules that do not support certain CRUD actions in Role UI / screens.
 * Customer Order Report has no delete action.
 */
export const MODULE_DISABLED_CRUD = {
  tracker_update: ["delete"],
  ai_chat: ["create", "update", "delete"],
  supplier_rfq: ["create", "delete"],
  supplier_po: ["create", "update", "delete"],
  supplier_gi: ["create", "update", "delete"],
  customer_sq: ["create", "update", "delete"],
  customer_so: ["create", "update", "delete"],
};

/** CRUD action buttons available for a module in Role Add/Edit. */
export function getModuleCrudActions(moduleKey) {
  const key = resolveModuleKey(moduleKey) || String(moduleKey || "").trim();
  const disabled = MODULE_DISABLED_CRUD[key] || [];
  return DEFAULT_CRUD_ACTIONS.filter((action) => !disabled.includes(action));
}
/** Mirrors Sidebar major modules → sub modules for Role Add/Edit. */
export const PERMISSION_GROUPS = [
  {
    id: "general",
    label: "General",
    icon: "fa-solid fa-gauge-high",
    permissions: [
      { key: "dashboard", label: "Dashboard" },
      { key: "ai_chat", label: "AI Search" },
    ],
  },
  {
    id: "procurement",
    label: "Procurement",
    icon: "fa-solid fa-boxes-stacked",
    permissions: [
      { key: "supply_master", label: "Supplier Master",
        children: [
    { key: "supplier_master_view", label: "View" },
    { key: "supplier_master_all", label: "All" },
  ],},
      { key: "procurement_tracker", label: "Purchase Request" },
      { key: "rfq", label: "RFQ" },
      { key: "po", label: "Purchase Order" },
      { key: "price_tracker", label: "Price List" },
    ],
  },
    {
    id: "LogisticsRfq",
    label: "Logistics RFQ",
    icon: "fa-solid fa-boxes-stacked",
    permissions: [
       { key: "supply_master", label: "Supplier Master",
        children: [
    { key: "supplier_master_view", label: "View" },
    { key: "supplier_master_all", label: "All" },
  ],},
      { key: "logistics", label: " Logistics RFQ" },
      
    ],
  },
  {
    id: "supplierRfq",
    label: "Supplier",
    icon: "fa-solid fa-file-invoice",
    permissions: [{ key: "supplier_rfq", label: "Supplier RFQ" },
      { key: "supplier_po", label: "Supplier PO" },
      { key: "supplier_gi", label: "Supplier GI" }
    ],
  },
   {
    id: "customer",
    label: "customer",
    icon: "fa-solid fa-file-invoice",
    permissions: [{ key: "customer_sq", label: "Customer Sales Quote" },
     { key: "customer_so", label: "Customer Sales Order" },
    
    ],
  },
  {
    id: "sales",
    label: "Sales",
    icon: "fa-solid fa-chart-line",
    permissions: [
      { key: "customer_master", label: "Customer Master",
   children: [
    { key: "customer_master_view", label: "View" },
    { key: "customer_master_all", label: "All" },
  ], },
      { key: "sales_tracker", label: "Sales Tracker" },
      { key: "sales_quotes", label: "Sales Quotes" },
      { key: "sales_order", label: "Sales Orders" },
      { key: SALES_APPROVAL_PERMISSION, label: "Sales Approval" },
    ],
  },
  {
    id: "ecatalogue",
    label: "E-Catalogue",
    icon: "fa-solid fa-book-open",
    permissions: [{ key: "ecatalogue", label: "E-Catalogue" }],
  },
  {
    id: "inventory",
    label: "Inventory List",
    icon: "fa-solid fa-boxes-stacked",
    permissions: [
      { key: "inventory_management", label: "Inventory Management" },
      { key: "goods_inwards", label: "Goods Inwards" },
      { key: "goods_outwards", label: "Goods Outwards" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: "fa-solid fa-gears",
    permissions: [
      { key: "so_converter", label: "SO Converter" },
      { key: "tracker_update", label: "Customer Order Report" },
      { key: "tracker_update_import", label: "Customer Order Report Import" },
      { key: "tracker_update_export", label: "Customer Order Report Export" },
      { key: "payment_tracker", label: "Payment Tracker" },
      { key: "freight_tracker", label: "Freight Tracker" },
      { key: "task_management", label: "Task Management" },
    ],
  },
  {
    id: "masterData",
    label: "Master Data",
    icon: "fa-solid fa-database",
    permissions: [
      { key: "category_management", label: "Category" },
      { key: "subcategory_management", label: "Subcategory" },
      { key: "material_management", label: "Material" },
      { key: "unit_management", label: "Unit" },
      { key: "attribute_management", label: "Brand/OEM" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: "fa-solid fa-building",
    permissions: [
      { key: "user_management", label: "Users" },
      { key: "role_management", label: "Roles" },
    ],
  },
];